import React, { useState } from "react";
import { useSelector } from "react-redux";
import type { All8PageContent } from "@apa/core-renderers";
import type { ContentFieldSelectFieldConfig, Path } from "../../Pages/pageComponentLibrary";
import type { IApplicationState } from "../../Redux/Reducers";
import { getValueAtPath } from "./pageEditorUtils";

type ContentFieldSelectControlProps = {
	editable: boolean;
	field: ContentFieldSelectFieldConfig;
	componentIndex: number;
	fullPath: Path;
	parentPath: Path;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	value: any;
};

const normalizeFieldType = (raw?: string) => (raw ?? "").trim().toLowerCase();

const normalizeVariableToken = (raw?: string) => {
	const value = (raw ?? "").toString().trim();
	if (value.startsWith("{") && value.endsWith("}")) {
		return value.slice(1, -1).trim();
	}
	return value;
};

const ContentFieldSelectControl: React.FC<ContentFieldSelectControlProps> = ({
	editable,
	field,
	componentIndex,
	fullPath,
	parentPath,
	previewComponents,
	updateComponentValue,
	value
}) => {
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);
	const [contentFieldFilter, setContentFieldFilter] = useState("");

	const resolveContentType = (contentTypeKey?: string | null) => {
		if (!contentTypeKey) return null;
		const normalized = contentTypeKey.toString().trim().toLowerCase();
		if (!normalized) return null;
		return contentTypeConfigs.find((config) => (config.id ?? "").toString().trim().toLowerCase() === normalized) ?? null;
	};

	const configField = field;
	const contentTypeKey = getValueAtPath(previewComponents[componentIndex], configField.contentTypePath);
	const config = resolveContentType((contentTypeKey ?? "").toString());
	const normalizedAllowed = (configField.fieldTypes ?? []).map(normalizeFieldType);
	const includeBase = configField.includeBaseFields ?? true;
	const valueFormat = configField.valueFormat ?? "raw";
	const options = [
		...(includeBase
			? [
					{ key: "id", title: "ID", type: "text" },
					{ key: "slug", title: "Slug", type: "text" },
					{ key: "path", title: "Path", type: "text" },
					{ key: "categoryPath", title: "Category path", type: "text" },
					{ key: "categoryTitle", title: "Category title", type: "text" },
					{ key: "taxonomies", title: "Taxonomies", type: "text" }
			  ]
			: []),
		...((config?.fields ?? []).map((field) => ({
			key: (field.key ?? field.title ?? "").trim(),
			title: (field.title ?? field.key ?? "").trim(),
			type: normalizeFieldType(field.type)
		})) ?? [])
	]
		.filter((opt) => opt.key)
		.filter((opt) => (normalizedAllowed.length ? normalizedAllowed.includes(opt.type) : true));
	const filteredOptions = options.filter((opt) => {
		const term = contentFieldFilter.trim().toLowerCase();
		if (!term) return true;
		return opt.key.toLowerCase().includes(term) || opt.title.toLowerCase().includes(term);
	});
	const selectedKey = normalizeVariableToken(value as string);
	const displayValue = options.some((opt) => opt.key === selectedKey) ? selectedKey : "";

	return (
		<div className="space-y-1">
			<label className="text-xs font-semibold text-slate-600">{field.label}</label>
			<input
				type="text"
				value={contentFieldFilter}
				onChange={(e) => setContentFieldFilter(e.target.value)}
				placeholder="Filter fields..."
				disabled={!editable}
				className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
			/>
			<select
				value={displayValue}
				onChange={(e) => {
					const nextKey = e.target.value;
					if (!nextKey && configField.allowEmpty) {
						updateComponentValue(componentIndex, fullPath, "");
						return;
					}
					const nextValue = valueFormat === "variable" ? `{${nextKey}}` : nextKey;
					updateComponentValue(componentIndex, fullPath, nextValue);
				}}
				disabled={!editable}
				className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
			>
				<option value="">{configField.allowEmpty ? "None" : "Select a field"}</option>
				{filteredOptions.map((option) => (
					<option key={option.key} value={option.key}>
						{option.title || option.key}
					</option>
				))}
			</select>
			{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
		</div>
	);
};

export default ContentFieldSelectControl;
